import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PRODUCT_DATA from "../data/products";
import { slugify, getAllProducts } from "../utils/helpers";
import { handleImageError } from "../utils/imageFallback";
import "./SearchBar.css";

const MAX_RESULTS = 8;

export default function SearchBar() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const ref = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const allProducts = useMemo(() => getAllProducts(PRODUCT_DATA), []);

  const trimmed = query.trim();

  const results = useMemo(() => {
    if (trimmed.length < 2) return [];
    const q = trimmed.toLowerCase();
    return allProducts
      .filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.brand?.toLowerCase().includes(q) ||
          p.category?.toLowerCase().includes(q) ||
          p.subcategory?.toLowerCase().includes(q)
      )
      .slice(0, MAX_RESULTS);
  }, [trimmed, allProducts]);

  /* Reset the keyboard cursor whenever the result set changes, so it can never
     point past the end of the list. */
  useEffect(() => {
    setHighlight(-1);
  }, [trimmed]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goTo = (product) => {
    navigate(`/category/${slugify(product.category)}`);
    setOpen(false);
    setQuery("");
    setHighlight(-1);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!results.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      goTo(results[highlight >= 0 ? highlight : 0]);
    }
  };

  const showPanel = open && trimmed.length >= 2;

  return (
    <div className="search" ref={ref}>
      <input
        ref={inputRef}
        type="search"
        placeholder="Search products, brands, categories..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        aria-label="Search products"
        role="combobox"
        aria-expanded={showPanel}
        aria-controls="search-results"
        aria-autocomplete="list"
        aria-activedescendant={
          showPanel && highlight >= 0 && highlight < results.length
            ? `search-result-${highlight}`
            : undefined
        }
      />

      <button className="search-btn" type="button" aria-label="Search" tabIndex={-1}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </button>

      {showPanel && (
        <div className="search-results" id="search-results" role="listbox">
          {results.length === 0 ? (
            <div className="search-empty">No products match “{trimmed}”.</div>
          ) : (
            results.map((p, i) => (
              <button
                key={`${p.category}-${p.subcategory}-${p.name}-${i}`}
                id={`search-result-${i}`}
                type="button"
                role="option"
                aria-selected={i === highlight}
                className={`search-result-item ${i === highlight ? "is-active" : ""}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => goTo(p)}
              >
                {p.img && (
                  <img src={p.img} alt="" loading="lazy" onError={handleImageError} />
                )}
                <div>
                  <div className="search-result-name">{p.name}</div>
                  <div className="search-result-meta">
                    {[p.brand, p.size].filter(Boolean).join(" · ") || p.category}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
